import React, { useState } from 'react';
import { DB } from '../data/database';
import { ROLES } from '../data/constants';
import { useLanguage } from '../translations/LanguageContext';
import { CheckIcon, PlusIcon } from './Icons';
import { Notifications, logActivity } from '../data/notifications';

const PRIORITIES = [
  { id: 'low', label: 'Low', color: 'var(--text3)' },
  { id: 'normal', label: 'Normal', color: 'var(--blue)' },
  { id: 'high', label: 'High', color: 'var(--orange)' },
  { id: 'urgent', label: 'Urgent', color: 'var(--red)' },
];

const STATUS_COLORS = {
  pending: 'var(--yellow)', in_progress: 'var(--blue)',
  completed: 'var(--green)', overdue: 'var(--red)', cancelled: 'var(--text3)',
};

function CreateTaskModal({ user, onClose, onSave }) {
  const { t } = useLanguage();
  const allUsers = DB.get('users') || [];
  const users = user.role === 'yard_manager'
    ? allUsers.filter(u => u.role === 'yard_operator' && u.yard === user.yard)
    : allUsers;
  const [form, setForm] = useState({
    title: '', description: '', assigned_to: '', due_date: '',
    priority: 'normal', entity_type: '', entity_ids: [],
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.assigned_to || !form.due_date) return;
    const tasks = DB.get('tasks') || [];
    const task = {
      id: DB.genId('TSK'),
      title: form.title.trim(),
      description: form.description.trim(),
      assigned_to: form.assigned_to,
      assigned_by: user.id,
      entity_type: form.entity_type || null,
      entity_ids: form.entity_ids,
      due_date: form.due_date,
      priority: form.priority,
      status: 'pending',
      completed_at: null,
      total_items: 0,
      completed_items: 0,
      attachments: [],
      notes: [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    DB.set('tasks', [...tasks, task]);
    const assignee = users.find(u => u.id === form.assigned_to);
    try {
      Notifications.add('unit_assigned', 'New Task Assigned',
        `New task: "${form.title}" — Due: ${form.due_date}`,
        { taskId: task.id }, form.assigned_to);
    } catch {}
    onSave();
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Create Task</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Title <span className="req">*</span></label>
              <input className="form-input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Price batch of 20 vehicles" required />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea className="form-input" rows={3} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Details..." style={{ resize: 'vertical' }} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Assign To <span className="req">*</span></label>
                <select className="form-select" value={form.assigned_to} onChange={e => set('assigned_to', e.target.value)} required>
                  <option value="">Select employee</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name} ({ROLES[u.role]?.name || u.role})</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Due Date <span className="req">*</span></label>
                <input type="date" className="form-input" value={form.due_date} onChange={e => set('due_date', e.target.value)} required />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Priority</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {PRIORITIES.map(p => (
                  <button key={p.id} type="button"
                    className={`req-type-btn${form.priority === p.id ? ' active' : ''}`}
                    style={{ fontSize: 11, padding: '4px 12px', color: form.priority === p.id ? p.color : undefined }}
                    onClick={() => set('priority', p.id)}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>{t('cancel')}</button>
            <button type="submit" className="btn btn-primary"><CheckIcon /> Create Task</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Tasks({ user }) {
  const { t } = useLanguage();
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useState('all');
  const [staffFilter, setStaffFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [, forceUpdate] = useState(0);

  const role = ROLES[user.role] || {};
  const isAdmin = user.role === 'head_admin' || user.role === 'admin';
  const allTasks = DB.get('tasks') || [];
  const users = DB.get('users') || [];
  const now = Date.now();

  // Auto-detect overdue
  allTasks.forEach(task => {
    if (['pending', 'in_progress'].includes(task.status) && task.due_date) {
      if (new Date(task.due_date).getTime() < now) {
        task.status = 'overdue';
      }
    }
  });

  // Filter
  let tasks = isAdmin ? allTasks : allTasks.filter(t => t.assigned_to === user.id);
  if (staffFilter) tasks = tasks.filter(t => t.assigned_to === staffFilter);
  if (filter !== 'all') tasks = tasks.filter(t => t.status === filter);

  const overdue = tasks.filter(t => t.status === 'overdue');
  const inProgress = tasks.filter(t => t.status === 'in_progress');
  const pending = tasks.filter(t => t.status === 'pending');
  const completed = tasks.filter(t => t.status === 'completed');

  const getUserName = (id) => users.find(u => u.id === id)?.name || id;

  const updateTask = (taskId, updates) => {
    DB.set('tasks', (DB.get('tasks') || []).map(t => t.id === taskId ? { ...t, ...updates, updated_at: new Date().toISOString() } : t));
    if (updates.status === 'completed') {
      const task = allTasks.find(t => t.id === taskId);
      if (task) logActivity.taskCompleted?.(task.title, user.name);
    }
    forceUpdate(v => v + 1);
  };

  const addNote = (taskId) => {
    if (!noteText.trim()) return;
    const task = allTasks.find(t => t.id === taskId);
    if (!task) return;
    const note = { by: user.id, name: user.name, text: noteText.trim(), timestamp: new Date().toISOString() };
    updateTask(taskId, { notes: [...(task.notes || []), note] });
    setNoteText('');
  };

  const renderTask = (task) => {
    const isExpanded = expandedId === task.id;
    const pri = PRIORITIES.find(p => p.id === task.priority);
    const daysLeft = task.due_date ? Math.ceil((new Date(task.due_date).getTime() - now) / 86400000) : null;
    const progress = task.total_items > 0 ? Math.round(task.completed_items / task.total_items * 100) : null;

    return (
      <div key={task.id} className="card" style={{ marginBottom: 8 }}>
        <div className="card-body" style={{ padding: '12px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }} onClick={() => setExpandedId(isExpanded ? null : task.id)}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>{task.title}</span>
                <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, fontWeight: 700, color: pri?.color, background: 'var(--bg3)' }}>{pri?.label}</span>
                <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, fontWeight: 700, color: STATUS_COLORS[task.status], background: 'var(--bg3)' }}>
                  {task.status.replace(/_/g, ' ')}
                </span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>
                Assigned to <strong>{getUserName(task.assigned_to)}</strong>
                {task.due_date && <span> · Due: {task.due_date}</span>}
                {daysLeft !== null && daysLeft < 0 && <span style={{ color: 'var(--red)', marginLeft: 4 }}>{Math.abs(daysLeft)}d overdue</span>}
                {daysLeft !== null && daysLeft >= 0 && daysLeft <= 2 && <span style={{ color: 'var(--yellow)', marginLeft: 4 }}>{daysLeft}d left</span>}
              </div>
              {progress !== null && (
                <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, height: 4, background: 'var(--bg3)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${progress}%`, background: progress >= 100 ? 'var(--green)' : 'var(--blue)', borderRadius: 2 }} />
                  </div>
                  <span style={{ fontSize: 10, color: 'var(--text3)' }}>{task.completed_items}/{task.total_items}</span>
                </div>
              )}
            </div>
            <span style={{ fontSize: 10, color: 'var(--text3)' }}>{isExpanded ? '▲' : '▼'}</span>
          </div>

          {isExpanded && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
              {task.description && <p style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 10 }}>{task.description}</p>}

              {/* Actions */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                {task.status === 'pending' && (
                  <button className="btn btn-primary btn-sm" style={{ fontSize: 10 }} onClick={() => updateTask(task.id, { status: 'in_progress' })}>
                    Start Working
                  </button>
                )}
                {['pending', 'in_progress', 'overdue'].includes(task.status) && (
                  <button className="btn btn-success btn-sm" style={{ fontSize: 10 }} onClick={() => updateTask(task.id, { status: 'completed', completed_at: new Date().toISOString() })}>
                    <CheckIcon /> Mark Complete
                  </button>
                )}
                {isAdmin && task.status !== 'cancelled' && task.status !== 'completed' && (
                  <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, color: 'var(--red)' }} onClick={() => updateTask(task.id, { status: 'cancelled' })}>
                    Cancel
                  </button>
                )}
              </div>

              {/* Notes */}
              {(task.notes || []).length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, marginBottom: 4 }}>Notes</div>
                  {task.notes.map((n, i) => (
                    <div key={i} style={{ fontSize: 11, padding: '4px 0', borderBottom: '1px solid var(--border)' }}>
                      <strong>{n.name}</strong>: {n.text}
                      <span style={{ color: 'var(--text3)', marginLeft: 6, fontSize: 9 }}>
                        {(() => { try { return new Date(n.timestamp).toLocaleDateString(); } catch { return ''; } })()}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Add note */}
              <div style={{ display: 'flex', gap: 6 }}>
                <input className="form-input" value={noteText} onChange={e => setNoteText(e.target.value)}
                  placeholder="Add a note..." style={{ flex: 1, fontSize: 11, padding: '4px 8px' }}
                  onKeyDown={e => { if (e.key === 'Enter') addNote(task.id); }} />
                <button className="btn btn-secondary btn-sm" style={{ fontSize: 10 }} onClick={() => addNote(task.id)}>Add</button>
              </div>

              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 8 }}>
                Created by {getUserName(task.assigned_by)} · {(() => { try { return new Date(task.created_at).toLocaleDateString(); } catch { return ''; } })()}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSection = (icon, title, items, color) => {
    if (items.length === 0) return null;
    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
          {icon} {title} ({items.length})
        </div>
        {items.map(renderTask)}
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <h2>Task Board</h2>
        {(isAdmin || user.role === 'yard_manager') && (
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            <PlusIcon /> Create Task
          </button>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {isAdmin && (
          <select className="inv-filter-select" value={staffFilter} onChange={e => setStaffFilter(e.target.value)}>
            <option value="">All Staff</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )}
        <select className="inv-filter-select" value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In Progress</option>
          <option value="overdue">Overdue</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <span style={{ fontSize: 11, color: 'var(--text3)', alignSelf: 'center' }}>{tasks.length} tasks</span>
      </div>

      {tasks.length === 0 ? (
        <div className="empty" style={{ marginTop: 20 }}>
          <div className="empty-title">No tasks {filter !== 'all' ? 'matching filter' : 'yet'}</div>
          {isAdmin && <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={() => setShowCreate(true)}><PlusIcon /> Create First Task</button>}
        </div>
      ) : (
        <>
          {renderSection('⏰', 'Overdue', overdue, 'var(--red)')}
          {renderSection('📌', 'In Progress', inProgress, 'var(--blue)')}
          {renderSection('⏳', 'Pending', pending, 'var(--yellow)')}
          {renderSection('✅', 'Completed', completed, 'var(--green)')}
        </>
      )}

      {showCreate && <CreateTaskModal user={user} onClose={() => setShowCreate(false)} onSave={() => forceUpdate(v => v + 1)} />}
    </div>
  );
}

export default Tasks;
